import React from 'react';
import { Clock } from 'lucide-react';
import { getEndTime } from '../../lib/utils';

/**
 * Visually prominent badge that highlights a booking's duration.
 *
 *   Prominent (default): used on booking flow + confirmation surfaces.
 *      ┌──────────────────────────────────────────┐
 *      │  ⏱  2-Hour Session  ·  09:00 - 11:00     │
 *      └──────────────────────────────────────────┘
 *
 *   Compact: used on dense admin cards / lists where space is tight.
 *      ┌──────────────┐
 *      │  ⏱ 2 hours   │
 *      └──────────────┘
 *
 * Props:
 *   - duration:  number of hours (>= 1). Required.
 *   - timeSlot:  optional "HH:MM-HH:MM" start slot — when provided in the
 *                "prominent" variant, the pill renders the full computed
 *                span e.g. "09:00 - 11:00".
 *   - variant:   "prominent" | "compact" (default: "prominent")
 *   - hideForOneHour: when true (default), hides itself for 1-hour bookings
 *                     because they're the implicit case. Set to false on
 *                     admin surfaces that always need an explicit chip.
 *   - testId:    optional data-testid suffix.
 */
export const DurationPill = ({
  duration,
  timeSlot,
  variant = 'prominent',
  hideForOneHour = true,
  testId,
}) => {
  const hrs = Number(duration || 1);
  if (hideForOneHour && hrs <= 1) return null;
  if (hrs < 1) return null;

  const label =
    hrs === 1 ? '1-Hour Session' : `${hrs}-Hour Session`;
  const compactLabel = hrs === 1 ? '1 hour' : `${hrs} hours`;

  const span =
    timeSlot && hrs > 1
      ? `${String(timeSlot).split('-')[0]} - ${getEndTime(timeSlot, hrs)}`
      : null;

  const prominentClass =
    'inline-flex items-center gap-2 px-4 py-2 rounded-full ' +
    'bg-blue-50 text-blue-800 ring-1 ring-blue-200 ' +
    'text-base font-bold tracking-tight';
  const compactClass =
    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full ' +
    'bg-blue-50 text-blue-800 ring-1 ring-blue-200 ' +
    'text-sm font-bold';

  if (variant === 'compact') {
    return (
      <span
        className={compactClass}
        data-testid={testId ? `duration-pill-${testId}` : undefined}
      >
        <Clock className="w-3.5 h-3.5" />
        {compactLabel}
      </span>
    );
  }

  return (
    <span
      className={prominentClass}
      data-testid={testId ? `duration-pill-${testId}` : undefined}
    >
      <Clock className="w-4 h-4" />
      <span>{label}</span>
      {span && (
        <>
          <span aria-hidden="true" className="opacity-60">·</span>
          <span className="font-semibold">{span}</span>
        </>
      )}
    </span>
  );
};

export default DurationPill;
